import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  DatePicker,
  EmptyState,
  Input,
  ReportTable,
  type ReportTablePaginationState,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { PermissionSlug } from '../constants/featureFlags';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { buildReportPopulationQuery } from '../components/reporting/reportPopulation';
import {
  exportScheduleDeviationReportReportsScheduleDeviationExportGet,
  getScheduleDeviationReportOptionsReportsScheduleDeviationOptionsGet,
  getScheduleDeviationReportReportsScheduleDeviationGet,
} from '../services/api/sdk.gen';
import type {
  AvailabilityFilter,
  ScheduleDeviationReportResult,
  ScheduleDeviationReportRow,
  ScheduleDeviationSortBy,
  SortOrder,
  TriStateFilter,
} from '../services/api/types.gen';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { useShallow } from 'zustand/react/shallow';
import { ReportMultiSelectFilter } from '../components/reporting/ReportMultiSelectFilter';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';
import {
  formatDateToISO,
  getTodayInPT,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import {
  formatReportEmptyValue,
  readChoiceParam,
  readDelimitedParam,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
} from '../utils/reportDisplay';

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const ALL_CLINICS_VALUE = '__all_clinics__';
const VALID_TRI_STATE: TriStateFilter[] = ['all', 'yes', 'no'];
const VALID_AVAILABILITY: AvailabilityFilter[] = ['all', 'yes', 'no'];
const VALID_SORT_BY: ScheduleDeviationSortBy[] = [
  'clinic_name',
  'patient_last_name',
  'patient_first_name',
  'mrn',
  'payer',
  'scheduled_date',
  'visit_date',
  'task',
  'assigned_clinician',
  'episode_active',
  'outside_episode',
  'same_week',
  'deviation_days',
];
const VALID_SORT_DIRECTIONS: SortOrder[] = ['asc', 'desc'];

type ReportOption = { id: string; label: string };
type ScheduleDeviationOptionsResponse = {
  clinics: ReportOption[];
  clinicians: ReportOption[];
  visit_types: ReportOption[];
};

type ScheduleDeviationQueryFilters = {
  dateFrom: string;
  dateTo: string;
  clinicId: string;
  clinicianId: string;
  visitTypes: string;
  episodeActive: TriStateFilter;
  outsideEpisode: AvailabilityFilter;
  sameWeek: AvailabilityFilter;
  q: string;
  includeTestRows: boolean;
  includeInactiveEpisodes: boolean;
  sortBy?: ScheduleDeviationSortBy;
  sortDirection?: SortOrder;
};

const columns = [
  {
    id: 'clinic_name',
    header: 'Clinic',
    accessor: (row: ScheduleDeviationReportRow) =>
      formatReportEmptyValue(row.clinic_name),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'patient_last_name',
    header: 'Patient Last',
    accessor: 'patient_last_name' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'patient_first_name',
    header: 'Patient First',
    accessor: 'patient_first_name' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: (row: ScheduleDeviationReportRow) =>
      formatReportEmptyValue(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'payer',
    header: 'Episode Payer',
    accessor: (row: ScheduleDeviationReportRow) =>
      formatReportEmptyValue(row.payer),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'scheduled_date',
    header: 'Scheduled Date',
    accessor: 'scheduled_date' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'visit_date',
    header: 'Visit Date',
    accessor: 'visit_date' as const,
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'task',
    header: 'Task',
    accessor: (row: ScheduleDeviationReportRow) =>
      formatReportEmptyValue(row.task),
    enableSorting: true,
    minWidth: 220,
  },
  {
    id: 'assigned_clinician',
    header: 'Assigned Clinician',
    accessor: (row: ScheduleDeviationReportRow) =>
      formatReportEmptyValue(row.assigned_clinician),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'episode_active',
    header: 'Episode Active',
    accessor: (row: ScheduleDeviationReportRow) =>
      row.episode_active ? 'Yes' : 'No',
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'outside_episode',
    header: 'Outside Episode',
    accessor: (row: ScheduleDeviationReportRow) =>
      availabilityLabel(row.outside_episode),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'same_week',
    header: 'Same Week',
    accessor: (row: ScheduleDeviationReportRow) =>
      availabilityLabel(row.same_week),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'deviation_days',
    header: 'Deviation Days',
    accessor: 'deviation_days' as const,
    enableSorting: true,
    align: 'right' as const,
    minWidth: 150,
  },
];

function availabilityLabel(value: string): string {
  if (value === 'yes') return 'Yes';
  if (value === 'no') return 'No';
  return formatReportEmptyValue(value);
}

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  const today = getTodayInPT();
  const toDate = parseISOToLocalDate(today);
  if (!toDate) return { dateFrom: today, dateTo: today };
  toDate.setDate(toDate.getDate() + 7);
  return { dateFrom: today, dateTo: formatDateToISO(toDate) };
}

function buildScheduleDeviationReportQuery(
  filters: ScheduleDeviationQueryFilters
) {
  return {
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    clinic_id: filters.clinicId || null,
    clinician_ids: readDelimitedParam(filters.clinicianId),
    visit_types: filters.visitTypes || null,
    episode_active: filters.episodeActive,
    outside_episode: filters.outsideEpisode,
    same_week: filters.sameWeek,
    q: filters.q || null,
    ...buildReportPopulationQuery(filters),
    sort_by: filters.sortBy ?? null,
    sort_direction: filters.sortDirection ?? null,
  };
}

const ScheduleDeviationReport = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  const reportsBackHref = useReportsBackHref();
  const [searchParams, setSearchParams] = useSearchParams();
  const [options, setOptions] = useState<ScheduleDeviationOptionsResponse>({
    clinics: [],
    clinicians: [],
    visit_types: [],
  });
  const [report, setReport] = useState<ScheduleDeviationReportResult | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasUrlDateRange = searchParams.has('from') || searchParams.has('to');
  const defaultDateRange = useMemo(() => getDefaultDateRange(), []);
  const dateFrom =
    searchParams.get('from') ??
    (hasUrlDateRange ? '' : defaultDateRange.dateFrom);
  const dateTo =
    searchParams.get('to') ?? (hasUrlDateRange ? '' : defaultDateRange.dateTo);
  const hasIncompleteRange = Boolean(dateFrom) !== Boolean(dateTo);
  const clinicId = searchParams.get('clinic') ?? '';
  const clinicianId = searchParams.get('clinician') ?? '';
  const visitTypes = searchParams.get('visit_type') ?? '';
  const episodeActive = readChoiceParam(
    searchParams.get('episode_active'),
    VALID_TRI_STATE,
    'all'
  );
  const outsideEpisode = readChoiceParam(
    searchParams.get('outside_episode'),
    VALID_AVAILABILITY,
    'all'
  );
  const sameWeek = readChoiceParam(
    searchParams.get('same_week'),
    VALID_AVAILABILITY,
    'all'
  );
  const q = searchParams.get('q') ?? '';
  const includeTestRows = searchParams.get('include_test_rows') === 'true';
  const includeInactiveEpisodes =
    searchParams.get('include_inactive_episodes') === 'true';
  const [searchDraft, setSearchDraft] = useState(q);
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = readChoiceParam(
    searchParams.get('size'),
    PAGE_SIZE_OPTIONS,
    25
  );
  const sortBy = readOptionalChoiceParam(
    searchParams.get('sort'),
    VALID_SORT_BY
  );
  const sortDirection = readOptionalChoiceParam(
    searchParams.get('dir'),
    VALID_SORT_DIRECTIONS
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>, resetPage = true) => {
      setSearchParams(current => {
        const next = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        if (resetPage) next.set('page', '1');
        return next;
      });
    },
    [setSearchParams]
  );

  useEffect(() => setSearchDraft(q), [q]);

  useEffect(() => {
    if (searchDraft === q) return;
    const timeout = window.setTimeout(() => {
      updateParams({ q: searchDraft || null });
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [q, searchDraft, updateParams]);

  const updateDateParam = useCallback(
    (field: 'from' | 'to', value: string | null) => {
      const updates: Record<string, string | null> = { [field]: value };
      if (!hasUrlDateRange) {
        updates[field === 'from' ? 'to' : 'from'] =
          field === 'from'
            ? defaultDateRange.dateTo
            : defaultDateRange.dateFrom;
      }
      updateParams(updates);
    },
    [defaultDateRange, hasUrlDateRange, updateParams]
  );

  const queryFilters = useMemo(
    () => ({
      dateFrom,
      dateTo,
      clinicId,
      clinicianId,
      visitTypes,
      episodeActive,
      outsideEpisode,
      sameWeek,
      q,
      includeTestRows,
      includeInactiveEpisodes,
      sortBy,
      sortDirection,
    }),
    [
      dateFrom,
      dateTo,
      clinicId,
      clinicianId,
      visitTypes,
      episodeActive,
      outsideEpisode,
      sameWeek,
      q,
      includeTestRows,
      includeInactiveEpisodes,
      sortBy,
      sortDirection,
    ]
  );

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    getScheduleDeviationReportOptionsReportsScheduleDeviationOptionsGet({
      query: {
        date_from: dateFrom || null,
        date_to: dateTo || null,
        ...buildReportPopulationQuery({
          includeTestRows,
          includeInactiveEpisodes,
        }),
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError(
            'Unable to load schedule deviation report. Please try again.'
          );
          return;
        }
        setOptions(response.data as ScheduleDeviationOptionsResponse);
      })
      .catch(error => {
        console.error(
          'Failed to load schedule deviation report options',
          error
        );
        if (!cancelled)
          setError(
            'Unable to load schedule deviation report. Please try again.'
          );
      });
    return () => {
      cancelled = true;
    };
  }, [
    hasPermission,
    dateFrom,
    dateTo,
    includeInactiveEpisodes,
    includeTestRows,
  ]);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    if (!dateFrom && !dateTo) {
      setReport(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    if (hasIncompleteRange) {
      setReport(null);
      setIsLoading(false);
      setError('Select both From and To dates.');
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getScheduleDeviationReportReportsScheduleDeviationGet({
      query: {
        ...buildScheduleDeviationReportQuery(queryFilters),
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError(
            getBusinessErrorMessage({
              response: {
                status: response.response?.status,
                data: response.error,
              },
            }) ?? 'Unable to load schedule deviation report. Please try again.'
          );
          setReport(null);
          return;
        }
        setReport(response.data);
      })
      .catch(error => {
        console.error('Failed to load schedule deviation report', error);
        if (!cancelled) {
          setError(
            getBusinessErrorMessage(error) ??
              'Unable to load schedule deviation report. Please try again.'
          );
          setReport(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    hasPermission,
    dateFrom,
    dateTo,
    hasIncompleteRange,
    queryFilters,
    page,
    pageSize,
  ]);

  const pagination: ReportTablePaginationState = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );

  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortDirection ?? 'asc' }
    : null;

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS))
    return <ReportsPermissionAlert />;

  const emptyState = error ? (
    <EmptyState role='alert' title={error} description='Please try again.' />
  ) : !dateFrom || !dateTo ? (
    <EmptyState
      title='Select a date range'
      description='Choose From and To dates to load schedule deviations.'
    />
  ) : (
    <EmptyState
      title='No schedule deviations'
      description='No visits in this date range have schedule deviations.'
    />
  );

  const download = async () => {
    if (!dateFrom || !dateTo || hasIncompleteRange) {
      setError('Select both From and To dates.');
      return;
    }
    try {
      const response =
        await exportScheduleDeviationReportReportsScheduleDeviationExportGet({
          query: buildScheduleDeviationReportQuery(queryFilters),
          responseType: 'blob' as const,
        } as never);
      if (response.error || !response.data) {
        setError(
          await getUserFacingApiMessageAsync(
            {
              response: {
                status: response.response?.status,
                data: response.response?.data ?? response.error,
              },
            },
            'Unable to download schedule deviation report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(
        response as never,
        'schedule-deviation-report.xlsx'
      );
    } catch (error) {
      console.error('Failed to download schedule deviation report', error);
      setError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to download schedule deviation report. Please try again.'
        )
      );
    }
  };

  const visitTypeOptions = useMemo(() => {
    const selectedValues = readDelimitedParam(visitTypes);
    const selected = new Set(selectedValues);
    return { selectedValues, selected };
  }, [visitTypes]);

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-bold'>Schedule Deviation</h1>
          <p className='text-sm text-muted-foreground'>
            Find visits documented outside their planned schedule.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void download()}
            disabled={isLoading || !dateFrom || !dateTo || hasIncompleteRange}
          >
            <Download className='h-4 w-4' /> Export
          </Button>
          <ReportPopulationControls
            includeTestRows={includeTestRows}
            includeInactiveEpisodes={includeInactiveEpisodes}
            onIncludeTestRowsChange={checked =>
              updateParams({ include_test_rows: checked ? 'true' : null })
            }
            onIncludeInactiveEpisodesChange={checked =>
              updateParams({
                include_inactive_episodes: checked ? 'true' : null,
              })
            }
          />
        </div>
      </div>

      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className='space-y-4 rounded-lg border bg-card p-4'>
        <div className='grid gap-3 md:grid-cols-4'>
          <div className='space-y-1 text-sm font-medium'>
            <label htmlFor='schedule-deviation-date-from'>From</label>
            <DatePicker
              id='schedule-deviation-date-from'
              date={parseISOToLocalDate(dateFrom)}
              datePickerSize='default'
              onDateChange={date =>
                updateDateParam('from', date ? formatDateToISO(date) : null)
              }
              showClearButton
            />
          </div>
          <div className='space-y-1 text-sm font-medium'>
            <label htmlFor='schedule-deviation-date-to'>To</label>
            <DatePicker
              id='schedule-deviation-date-to'
              date={parseISOToLocalDate(dateTo)}
              datePickerSize='default'
              onDateChange={date =>
                updateDateParam('to', date ? formatDateToISO(date) : null)
              }
              showClearButton
            />
          </div>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='schedule-deviation-clinic-filter'
          >
            Clinic
            <Select
              value={clinicId || ALL_CLINICS_VALUE}
              onValueChange={value =>
                updateParams({
                  clinic: value === ALL_CLINICS_VALUE ? null : value,
                })
              }
            >
              <SelectTrigger
                id='schedule-deviation-clinic-filter'
                selectSize='default'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLINICS_VALUE}>All clinics</SelectItem>
                {options.clinics.map(option => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <ReportMultiSelectFilter
            label='Assigned clinician'
            value={clinicianId}
            options={options.clinicians}
            allLabel='All clinicians'
            deferApply
            onChange={value => updateParams({ clinician: value || null })}
          />
        </div>
        <div className='grid gap-3 md:grid-cols-4'>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='schedule-deviation-episode-active-filter'
          >
            Episode Active
            <Select
              value={episodeActive}
              onValueChange={value =>
                updateParams({
                  episode_active: value || null,
                  ...(value === 'no'
                    ? { include_inactive_episodes: 'true' }
                    : {}),
                })
              }
            >
              <SelectTrigger
                id='schedule-deviation-episode-active-filter'
                selectSize='default'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='yes'>Active</SelectItem>
                <SelectItem value='no'>Not active</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='schedule-deviation-outside-episode-filter'
          >
            Outside Episode
            <Select
              value={outsideEpisode}
              onValueChange={value =>
                updateParams({ outside_episode: value || null })
              }
            >
              <SelectTrigger
                id='schedule-deviation-outside-episode-filter'
                selectSize='default'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='yes'>Yes</SelectItem>
                <SelectItem value='no'>No</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='schedule-deviation-same-week-filter'
          >
            Same Week
            <Select
              value={sameWeek}
              onValueChange={value =>
                updateParams({ same_week: value || null })
              }
            >
              <SelectTrigger
                id='schedule-deviation-same-week-filter'
                selectSize='default'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All</SelectItem>
                <SelectItem value='yes'>Yes</SelectItem>
                <SelectItem value='no'>No</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className='space-y-1 text-sm font-medium'>
            Search
            <Input
              value={searchDraft}
              onChange={event => setSearchDraft(event.target.value)}
              placeholder='Patient, MRN, clinician, or payer'
            />
          </label>
        </div>
        <div className='space-y-2'>
          <span className='text-sm font-medium'>Task</span>
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              variant={visitTypes ? 'outline' : 'default'}
              size='sm'
              onClick={() => updateParams({ visit_type: null })}
            >
              All tasks
            </Button>
            {options.visit_types.map(option => {
              const checked = visitTypeOptions.selected.has(option.id);
              return (
                <Button
                  key={option.id}
                  type='button'
                  variant={checked ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => {
                    const next = new Set(visitTypeOptions.selectedValues);
                    if (checked) next.delete(option.id);
                    else next.add(option.id);
                    updateParams({ visit_type: [...next].join(',') || null });
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.row_id}
        caption='Schedule deviation report results'
        sorting={sorting}
        onSortingChange={next => {
          updateParams(
            next
              ? { sort: next.id, dir: next.direction }
              : { sort: null, dir: null }
          );
        }}
        isLoading={isLoading}
        emptyState={emptyState}
        pagination={{
          ...pagination,
          total: report?.total ?? 0,
          totalPages: Math.ceil((report?.total ?? 0) / pageSize),
        }}
        onPaginationChange={next => {
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set('page', String(next.page));
            params.set('size', String(next.pageSize));
            return params;
          });
        }}
      />
    </div>
  );
};

export default ScheduleDeviationReport;
